import generatedCapabilities from "@/generated/facilitykpi-semantic-capabilities.json";
import { api } from "@redux/api";

let activeCapabilities = generatedCapabilities;
let loadingPromise = null;

const validCapabilities = (payload) =>
  payload?.schema_version === generatedCapabilities.schema_version &&
  payload?.resources &&
  typeof payload.resources === "object";

export const getSemanticCapabilities = () => activeCapabilities;

export const getResourceCapabilities = (resource, payload = activeCapabilities) =>
  payload?.resources?.[resource] || generatedCapabilities.resources[resource] || {};

export const getWorkOrderStatusValues = (payload = activeCapabilities) =>
  getResourceCapabilities("workorders", payload)?.value_domains?.status?.values || [];

export const getWorkOrderDomainValues = (field, payload = activeCapabilities) =>
  getResourceCapabilities("workorders", payload)?.value_domains?.[field]?.values || [];

export const getWorkOrderDomainDefault = (field, payload = activeCapabilities) => {
  const domain = getResourceCapabilities("workorders", payload)?.value_domains?.[field];
  return domain?.default || domain?.values?.[0] || "";
};

export const formatWorkOrderDomainValue = (field, value, payload = activeCapabilities) => {
  const domain = getResourceCapabilities("workorders", payload)?.value_domains?.[field];
  if (domain?.labels?.[value]) return domain.labels[value];
  return field === "status" ? formatWorkOrderStatus(value) : value;
};

export const getWorkOrderDomainValueEnum = (field, payload = activeCapabilities) =>
  Object.fromEntries(
    getWorkOrderDomainValues(field, payload).map((value) => [
      value,
      { text: formatWorkOrderDomainValue(field, value, payload) },
    ])
  );

export const getWorkOrderTerminalStatuses = (payload = activeCapabilities) =>
  getResourceCapabilities("workorders", payload)?.lifecycle?.terminal_states || [];

export const getWorkOrderInitialStatus = (payload = activeCapabilities) => {
  const resource = getResourceCapabilities("workorders", payload);
  return resource?.lifecycle?.initial_state || getWorkOrderStatusValues(payload)[0] || "";
};

export const isWorkOrderTerminalStatus = (status, payload = activeCapabilities) =>
  getWorkOrderTerminalStatuses(payload).includes(status);

export const getWorkOrderOpenStatuses = (payload = activeCapabilities) => {
  const resource = getResourceCapabilities("workorders", payload);
  const openPopulation = (resource.semantic_populations || []).find(
    (item) => item.id === resource.lifecycle?.open_population
  );
  const terminal = openPopulation?.filters?.find(
    (item) => item.field === resource.lifecycle?.state_field && item.operator === "not_in"
  )?.value || getWorkOrderTerminalStatuses(payload);
  return getWorkOrderStatusValues(payload).filter((status) => !terminal.includes(status));
};

export const formatWorkOrderStatus = (status) =>
  status === "InProgress"
    ? "In Progress"
    : status === "On-Hold"
    ? "On-hold"
    : status;

export const getWorkOrderStatusValueEnum = (payload = activeCapabilities) =>
  getWorkOrderDomainValueEnum("status", payload);

export const loadSemanticCapabilities = () => {
  if (loadingPromise) return loadingPromise;
  loadingPromise = api
    .getSemanticCapabilities()
    .then((response) => response?.data || response)
    .then((payload) => {
      if (!validCapabilities(payload)) {
        throw new Error("Semantic capabilities response is incompatible");
      }
      activeCapabilities = payload;
      return activeCapabilities;
    })
    .catch(() => activeCapabilities)
    .finally(() => {
      loadingPromise = null;
    });
  return loadingPromise;
};
